import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box, Typography, Card, CardContent, Grid, Chip, Tab, Tabs,
    CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, IconButton, Tooltip, Divider,
    TextField, Button, MenuItem, Select, FormControl, InputLabel,
    InputAdornment, Stack, LinearProgress,
} from '@mui/material';
import { Icon } from '@iconify/react';
import downloadFill from '@iconify/icons-eva/download-fill';
import fileTextFill from '@iconify/icons-eva/file-text-fill';
import syndicationApi from '../../utils/syndicationApi';
import GatewayImpactSimulator from '../../components/syndication/GatewayImpactSimulator';
import SyndicationDocumentViewer from '../../components/syndication/SyndicationDocumentViewer';

const STATUS_COLORS = {
    active: 'success', qa_phase: 'info', commitment_phase: 'warning',
    allocation_phase: 'secondary', completed: 'default',
};

function TabPanel({ children, value, index }) {
    return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

export default function InvestorDealRoom() {
    const { dealCode } = useParams();
    const [deal, setDeal] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [interest, setInterest] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [commitment, setCommitment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [tab, setTab] = useState(0);
    const [downloading, setDownloading] = useState(null);
    const [capTable, setCapTable] = useState([]);
    // Q&A state
    const [newQuestion, setNewQuestion] = useState('');
    const [askingQuestion, setAskingQuestion] = useState(false);
    // Commitment state
    const [commitForm, setCommitForm] = useState({ decision: 'soft', amount: '', conditions_text: '' });
    const [submittingCommitment, setSubmittingCommitment] = useState(false);
    const [modifyMode, setModifyMode] = useState(false);
    const [modifyForm, setModifyForm] = useState({ amount: '', decision: '', conditions_text: '' });
    const [modifying, setModifying] = useState(false);

    useEffect(() => {
        fetchDealRoom();
    }, [dealCode]);

    const fetchDealRoom = async () => {
        try {
            const [dealRes, docsRes, interestRes, qaRes, commitRes, capRes] = await Promise.all([
                syndicationApi.get(`investor/deals/${dealCode}/`),
                syndicationApi.get(`investor/deals/${dealCode}/documents/`),
                syndicationApi.get(`investor/deals/${dealCode}/interest/`),
                syndicationApi.get(`investor/deals/${dealCode}/qa/`).catch(() => ({ data: { data: [] } })),
                syndicationApi.get(`investor/deals/${dealCode}/commit/`).catch(() => ({ data: { data: null } })),
                syndicationApi.get(`investor/deals/${dealCode}/cap-table/`).catch(() => ({ data: { data: [] } })),
            ]);
            setDeal(dealRes.data?.data);
            setDocuments(docsRes.data?.data || []);
            setInterest(interestRes.data?.data);
            setQuestions(qaRes.data?.data || []);
            setCommitment(commitRes.data?.data);
            setCapTable(capRes.data?.data || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load deal room.');
        } finally {
            setLoading(false);
        }
    };

    const handleAskQuestion = async () => {
        if (!newQuestion.trim()) return;
        setAskingQuestion(true);
        try {
            await syndicationApi.post(`investor/deals/${dealCode}/qa/ask/`, { question_text: newQuestion });
            setNewQuestion('');
            const qaRes = await syndicationApi.get(`investor/deals/${dealCode}/qa/`);
            setQuestions(qaRes.data?.data || []);
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to submit question.');
        } finally {
            setAskingQuestion(false);
        }
    };

    const handleSubmitCommitment = async () => {
        if (commitForm.decision !== 'no' && !commitForm.amount) return;
        setSubmittingCommitment(true);
        try {
            const payload = commitForm.decision === 'no'
                ? { ...commitForm, amount: 0 }
                : commitForm;
            const res = await syndicationApi.post(`investor/deals/${dealCode}/commit/submit/`, payload);
            setCommitment(res.data?.data);
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to submit commitment.');
        } finally {
            setSubmittingCommitment(false);
        }
    };

    const handleWithdrawCommitment = async () => {
        if (!window.confirm('Are you sure you want to withdraw your commitment?')) return;
        try {
            await syndicationApi.post(`investor/deals/${dealCode}/commit/withdraw/`);
            setCommitment(null);
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to withdraw.');
        }
    };

    const handleModifyCommitment = async () => {
        setModifying(true);
        try {
            const payload = {};
            if (modifyForm.amount) payload.amount = modifyForm.amount;
            if (modifyForm.decision) payload.decision = modifyForm.decision;
            if (modifyForm.conditions_text !== undefined) payload.conditions_text = modifyForm.conditions_text;
            const res = await syndicationApi.patch(`investor/deals/${dealCode}/commit/modify/`, payload);
            setCommitment(res.data?.data);
            setModifyMode(false);
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to modify commitment.');
        } finally {
            setModifying(false);
        }
    };

    const handleConvertCommitment = async (newDecision) => {
        if (!window.confirm(`Convert your commitment to ${newDecision}?`)) return;
        try {
            const res = await syndicationApi.post(`investor/deals/${dealCode}/commit/convert/`, { new_decision: newDecision });
            setCommitment(res.data?.data);
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to convert commitment.');
        }
    };

    const handleDownload = async (doc) => {
        setDownloading(doc.id);
        try {
            const res = await syndicationApi.get(
                `investor/deals/${dealCode}/documents/${doc.id}/download/`,
                { responseType: 'blob' }
            );
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            const contentDisp = res.headers['content-disposition'];
            const filename = contentDisp
                ? contentDisp.split('filename=')[1]?.replace(/"/g, '')
                : `${doc.title}.pdf`;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            alert('Download failed. Please try again.');
        } finally {
            setDownloading(null);
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
    }
    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }
    if (!deal) return null;

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>{deal.title}</Typography>
                    <Typography variant="body1" color="text.secondary">{deal.company_name}</Typography>
                </Box>
                <Chip label={deal.status?.replace(/_/g, ' ')} color={STATUS_COLORS[deal.status] || 'default'} />
            </Box>

            {/* Tabs */}
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tab label="Overview" />
                <Tab label={`Documents (${documents.length})`} />
                <Tab label={`Q&A (${questions.length})`} />
                <Tab label="Commitment" />
                <Tab label={`Cap Table (${capTable.length})`} />
                <Tab label="Impact Simulator" />
            </Tabs>

            {/* Overview Tab */}
            <TabPanel value={tab} index={0}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Card>
                            <CardContent>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Deal Details</Typography>
                                <Grid container spacing={2}>
                                    {[
                                        ['Total Raise', `${deal.currency_label || ''} ${Number(deal.total_raise || 0).toLocaleString()}`],
                                        ['Co-investor Allocation', `${deal.currency_label || ''} ${Number(deal.coinvestor_allocation || 0).toLocaleString()}`],
                                        ['Min Ticket', `${deal.currency_label || ''} ${Number(deal.min_ticket_size || 0).toLocaleString()}`],
                                        ['Max Ticket', `${deal.currency_label || ''} ${Number(deal.max_ticket_size || 0).toLocaleString()}`],
                                        ['Pre-Money Valuation', deal.pre_money_valuation ? `${deal.currency_label || ''} ${Number(deal.pre_money_valuation).toLocaleString()}` : '—'],
                                        ['Post-Money Valuation', deal.post_money_valuation ? `${deal.currency_label || ''} ${Number(deal.post_money_valuation).toLocaleString()}` : '—'],
                                        ['Equity Offered', deal.equity_offered_percent ? `${deal.equity_offered_percent}%` : '—'],
                                        ['Instrument', deal.instrument_type || '—'],
                                    ].map(([label, value]) => (
                                        <Grid item xs={6} sm={4} key={label}>
                                            <Typography variant="caption" color="text.secondary">{label}</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
                                        </Grid>
                                    ))}
                                </Grid>
                                {deal.description && (
                                    <Box sx={{ mt: 3 }}>
                                        <Typography variant="caption" color="text.secondary">Description</Typography>
                                        <Typography variant="body2" sx={{ mt: 0.5 }}>{deal.description}</Typography>
                                    </Box>
                                )}
                                {deal.operator_value_description && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="caption" color="text.secondary">Operator Value Description</Typography>
                                        <Typography variant="body2" sx={{ mt: 0.5 }}>{deal.operator_value_description}</Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card sx={{ mb: 2 }}>
                            <CardContent>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Timeline</Typography>
                                {[
                                    ['Commitment Deadline', deal.commitment_deadline],
                                    ['Q&A Start', deal.qa_start_at],
                                    ['Q&A End', deal.qa_end_at],
                                ].map(([label, date]) => (
                                    <Box key={label} sx={{ mb: 1 }}>
                                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {date ? new Date(date).toLocaleDateString('en-IN', {
                                                year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                                            }) : '—'}
                                        </Typography>
                                    </Box>
                                ))}
                            </CardContent>
                        </Card>

                        {interest && (
                            <Card>
                                <CardContent>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Aggregate Interest</Typography>
                                    {[
                                        ['Total Committed', interest.total_committed],
                                        ['Investors', interest.investor_count],
                                        ['Binding', `${interest.binding_count} (${Number(interest.binding_total || 0).toLocaleString()})`],
                                        ['Soft', `${interest.soft_count} (${Number(interest.soft_total || 0).toLocaleString()})`],
                                        ['Conditional', `${interest.conditional_count} (${Number(interest.conditional_total || 0).toLocaleString()})`],
                                    ].map(([label, value]) => (
                                        <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography variant="body2" color="text.secondary">{label}</Typography>
                                            <Typography variant="body2" fontWeight={600}>{value}</Typography>
                                        </Box>
                                    ))}
                                </CardContent>
                            </Card>
                        )}
                    </Grid>
                </Grid>
            </TabPanel>

            {/* Documents Tab */}
            <TabPanel value={tab} index={1}>
                {documents.length === 0 ? (
                    <Card>
                        <CardContent sx={{ textAlign: 'center', py: 4 }}>
                            <Typography color="text.secondary">No documents available yet.</Typography>
                        </CardContent>
                    </Card>
                ) : (
                    <TableContainer component={Paper} variant="outlined">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Document</TableCell>
                                    <TableCell>Category</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell align="right">Size</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {documents.map((doc) => (
                                    <TableRow key={doc.id} hover>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Icon icon={fileTextFill} width={20} color="#666" />
                                                <Typography variant="body2" fontWeight={600}>{doc.title}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={doc.category} size="small" variant="outlined" />
                                        </TableCell>
                                        <TableCell>{doc.file_type?.toUpperCase()}</TableCell>
                                        <TableCell align="right">
                                            {doc.file_size ? `${(doc.file_size / 1024).toFixed(1)} KB` : '—'}
                                        </TableCell>
                                        <TableCell align="center">
                                            {doc.file ? (
                                                <SyndicationDocumentViewer
                                                    fileUrl={doc.file}
                                                    fileType={doc.file_type}
                                                    title={doc.title}
                                                    onDownload={() => handleDownload(doc)}
                                                    downloading={downloading === doc.id}
                                                    iconOnly
                                                />
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">—</Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </TabPanel>

            {/* Q&A Tab */}
            <TabPanel value={tab} index={2}>
                {deal.status === 'qa_phase' && (
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Ask a Question</Typography>
                            <TextField
                                fullWidth multiline rows={3} placeholder="Type your question..."
                                value={newQuestion} onChange={(e) => setNewQuestion(e.target.value)}
                                sx={{ mb: 1.5 }}
                            />
                            <Button variant="contained" onClick={handleAskQuestion}
                                disabled={askingQuestion || !newQuestion.trim()} size="small">
                                {askingQuestion ? 'Submitting...' : 'Submit Question'}
                            </Button>
                        </CardContent>
                    </Card>
                )}
                {questions.length === 0 ? (
                    <Card><CardContent sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">No Q&A available yet.</Typography>
                    </CardContent></Card>
                ) : (
                    <Stack spacing={2}>
                        {questions.map((q) => (
                            <Card key={q.id} variant="outlined">
                                <CardContent>
                                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                                        Q: {q.question_text}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Asked {q.asked_at ? new Date(q.asked_at).toLocaleDateString() : ''}
                                    </Typography>
                                    {q.answers && q.answers.length > 0 ? (
                                        q.answers.map((a, idx) => (
                                            <Box key={idx} sx={{ mt: 1.5, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                                                <Typography variant="body2">A: {a.answer_text}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    by {a.answered_by_role} {a.answered_at ? `— ${new Date(a.answered_at).toLocaleDateString()}` : ''}
                                                </Typography>
                                            </Box>
                                        ))
                                    ) : (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                                            Awaiting answer...
                                        </Typography>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                )}
            </TabPanel>

            {/* Commitment Tab */}
            <TabPanel value={tab} index={3}>
                {commitment ? (
                    <Card>
                        <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Your Commitment</Typography>
                            <Grid container spacing={2}>
                                {[
                                    ['Type', commitment.decision],
                                    ['Amount', Number(commitment.amount || 0).toLocaleString()],
                                    ['Status', commitment.is_withdrawn ? 'Withdrawn' : commitment.is_locked ? 'Locked' : 'Active'],
                                    ['Signature', commitment.signature_confirmed ? 'Confirmed' : 'Pending'],
                                ].map(([label, val]) => (
                                    <Grid item xs={6} sm={3} key={label}>
                                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{val}</Typography>
                                    </Grid>
                                ))}
                                {commitment.conditions_text && (
                                    <Grid item xs={12}>
                                        <Typography variant="caption" color="text.secondary">Conditions</Typography>
                                        <Typography variant="body2">{commitment.conditions_text}</Typography>
                                    </Grid>
                                )}
                                {commitment.allocated_amount != null && (
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Allocated Amount</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {Number(commitment.allocated_amount).toLocaleString()}
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                            {!commitment.is_locked && !commitment.is_withdrawn && (
                                <Box sx={{ mt: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <Button variant="outlined" size="small" onClick={() => {
                                        setModifyForm({ amount: commitment.amount, decision: commitment.decision, conditions_text: commitment.conditions_text || '' });
                                        setModifyMode(true);
                                    }}>
                                        Modify
                                    </Button>
                                    {commitment.decision === 'conditional' && (
                                        <>
                                            <Button variant="outlined" color="primary" size="small" onClick={() => handleConvertCommitment('soft')}>
                                                Convert to Soft
                                            </Button>
                                            <Button variant="outlined" color="primary" size="small" onClick={() => handleConvertCommitment('binding')}>
                                                Convert to Binding
                                            </Button>
                                        </>
                                    )}
                                    <Button variant="outlined" color="error" size="small" onClick={handleWithdrawCommitment}>
                                        Withdraw
                                    </Button>
                                </Box>
                            )}
                            {modifyMode && (
                                <Card variant="outlined" sx={{ mt: 2 }}>
                                    <CardContent>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Modify Commitment</Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={4}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Decision</InputLabel>
                                                    <Select value={modifyForm.decision}
                                                        onChange={(e) => setModifyForm(f => ({ ...f, decision: e.target.value }))}
                                                        label="Decision">
                                                        <MenuItem value="soft">Soft</MenuItem>
                                                        <MenuItem value="binding">Binding</MenuItem>
                                                        <MenuItem value="conditional">Conditional</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <TextField fullWidth size="small" label="Amount" type="number"
                                                    value={modifyForm.amount}
                                                    onChange={(e) => setModifyForm(f => ({ ...f, amount: e.target.value }))} />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <TextField fullWidth size="small" label="Conditions"
                                                    value={modifyForm.conditions_text}
                                                    onChange={(e) => setModifyForm(f => ({ ...f, conditions_text: e.target.value }))} />
                                            </Grid>
                                        </Grid>
                                        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                                            <Button variant="contained" size="small" onClick={handleModifyCommitment} disabled={modifying}>
                                                {modifying ? 'Saving...' : 'Save Changes'}
                                            </Button>
                                            <Button variant="outlined" size="small" onClick={() => setModifyMode(false)}>Cancel</Button>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            )}
                        </CardContent>
                    </Card>
                ) : (
                    <Card>
                        <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Submit Commitment</Typography>
                            {deal.status !== 'commitment_phase' ? (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    Commitment window is not currently open for this deal.
                                </Alert>
                            ) : (
                                <>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Decision Type</InputLabel>
                                                <Select value={commitForm.decision}
                                                    onChange={(e) => setCommitForm(f => ({ ...f, decision: e.target.value }))}
                                                    label="Decision Type">
                                                    <MenuItem value="soft">Soft Interest</MenuItem>
                                                    <MenuItem value="binding">Binding Commitment</MenuItem>
                                                    <MenuItem value="conditional">Conditional</MenuItem>
                                                    <MenuItem value="no">No / Pass</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        {commitForm.decision !== 'no' && (
                                            <Grid item xs={12} sm={6}>
                                                <TextField fullWidth size="small" label="Amount" type="number"
                                                    value={commitForm.amount}
                                                    onChange={(e) => setCommitForm(f => ({ ...f, amount: e.target.value }))}
                                                    InputProps={{
                                                        startAdornment: <InputAdornment position="start">{deal.currency_label || '$'}</InputAdornment>,
                                                        inputProps: { min: deal.min_ticket_size || 0, max: deal.max_ticket_size || undefined }
                                                    }}
                                                />
                                            </Grid>
                                        )}
                                        {commitForm.decision === 'conditional' && (
                                            <Grid item xs={12}>
                                                <TextField fullWidth size="small" label="Conditions"
                                                    multiline rows={2} value={commitForm.conditions_text}
                                                    onChange={(e) => setCommitForm(f => ({ ...f, conditions_text: e.target.value }))}
                                                />
                                            </Grid>
                                        )}
                                    </Grid>
                                    <Box sx={{ mt: 2 }}>
                                        <Button variant="contained" onClick={handleSubmitCommitment}
                                            disabled={submittingCommitment || (commitForm.decision !== 'no' && !commitForm.amount)}>
                                            {submittingCommitment ? 'Submitting...' : commitForm.decision === 'no' ? 'Confirm Pass' : 'Submit Commitment'}
                                        </Button>
                                    </Box>
                                </>
                            )}
                        </CardContent>
                    </Card>
                )}
            </TabPanel>

            {/* Cap Table Tab */}
            <TabPanel value={tab} index={4}>
                {capTable.length === 0 ? (
                    <Card><CardContent sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">No cap table data available yet.</Typography>
                    </CardContent></Card>
                ) : (
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Holder</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell align="right">Amount</TableCell>
                                    <TableCell align="right">Equity %</TableCell>
                                    <TableCell>Instrument</TableCell>
                                    <TableCell>Phase</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {capTable.map((entry) => (
                                    <TableRow key={entry.id} hover>
                                        <TableCell><Typography variant="body2" fontWeight={600}>{entry.holder_name}</Typography></TableCell>
                                        <TableCell><Chip label={entry.holder_type} size="small" variant="outlined" /></TableCell>
                                        <TableCell align="right">{Number(entry.amount || 0).toLocaleString()}</TableCell>
                                        <TableCell align="right">{entry.equity_percent}%</TableCell>
                                        <TableCell>{entry.instrument_type}</TableCell>
                                        <TableCell><Chip label={entry.phase === 'pre_deal' ? 'Pre-Deal' : 'Post-Deal'} size="small" /></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </TabPanel>

            {/* Impact Simulator Tab */}
            <TabPanel value={tab} index={5}>
                <GatewayImpactSimulator deal={deal} />
            </TabPanel>
        </Box>
    );
}
